/**
 * Who is using one of the brewer's ingredients.
 *
 * Two surfaces ask this and must agree: the reducer's guard on
 * `customIngredient.remove`, which refuses while anything points at the id, and
 * the Ingredients page's delete dialog, which tells the brewer *what* points at
 * it — "2 lots, 3 recipes" — and offers the one cascade there is. One function,
 * so the dialog never promises a deletion the reducer will refuse.
 *
 * ## Live documents only
 *
 * A batch's `recipeSnapshot` and a named `recipeSnapshot` are frozen copies:
 * "editing the source recipe afterwards cannot move a single number inside a
 * snapshot" (`document/recipe-snapshot.ts`), and deleting an ingredient moves
 * nothing inside them either, because the numbers were copied and an id that no
 * longer resolves reads as `null` (D-IL8). So they are not scanned, and a brewer
 * can delete a malt they brewed with last year without the log of that brew
 * standing in the way.
 */

import type { CommandPayloadMap } from '../commands/types';
import {
  type CustomIngredientDoc,
  customIngredientDisplayName,
  customIngredientMaker,
} from '../document/custom-ingredient';
import type { InventoryItemDoc, InventoryKind } from '../document/inventory';
import type { RecipeDoc } from '../document/recipe';
import type { WorkspaceState } from '../document/workspace';
import { normalizeIngredientName } from './availability';

/** One live recipe that uses the ingredient, and how many of its rows do. */
export interface CustomIngredientRecipeUse {
  readonly recipeId: string;
  readonly name: string;
  /** Rows carrying the link — a hop added at 60 and again at 5 counts twice. */
  readonly rows: number;
}

/** Everything in the workspace that points at one custom ingredient. */
export interface CustomIngredientUsage {
  readonly ingredientId: string;
  /** Every linked lot, empty ones included, sorted by id. */
  readonly lots: readonly InventoryItemDoc[];
  /** Every live recipe with a linked row, sorted by name then id. */
  readonly recipes: readonly CustomIngredientRecipeUse[];
}

/** How many rows of a recipe, across its four lists, carry the link. */
function linkedRows(recipe: RecipeDoc, ingredientId: string): number {
  let rows = 0;
  for (const list of [recipe.fermentables, recipe.hops, recipe.yeasts, recipe.miscs]) {
    for (const item of list) if (item.ingredientId === ingredientId) rows += 1;
  }
  return rows;
}

/**
 * Everything that points at `ingredientId`.
 *
 * Sorted, both halves, so the reducer's message and the dialog's list read the
 * same on every device: `Object.values` order is insertion order, which two
 * replicas of one log share, but a sorted answer is the one that cannot be
 * argued with.
 */
export function customIngredientUsage(
  state: WorkspaceState,
  ingredientId: string,
): CustomIngredientUsage {
  const lots = Object.values(state.inventory)
    .filter((lot) => lot.ingredientId === ingredientId)
    .sort((a, b) => a.id.localeCompare(b.id));

  const recipes: CustomIngredientRecipeUse[] = [];
  for (const recipe of Object.values(state.recipes)) {
    const rows = linkedRows(recipe, ingredientId);
    if (rows > 0) recipes.push({ recipeId: recipe.id, name: recipe.name, rows });
  }
  recipes.sort((a, b) => a.name.localeCompare(b.name) || a.recipeId.localeCompare(b.recipeId));

  return { ingredientId, lots, recipes };
}

// ---------------------------------------------------------------------------
// The link pass
// ---------------------------------------------------------------------------

/**
 * One command of the link pass: a lot update or a row update, as a type +
 * payload pair the store's `dispatchAll` accepts unchanged.
 */
export type IngredientLinkStep =
  | { readonly type: 'inventory.update'; readonly payload: CommandPayloadMap['inventory.update'] }
  | {
      readonly type: 'recipe.updateFermentable';
      readonly payload: CommandPayloadMap['recipe.updateFermentable'];
    }
  | { readonly type: 'recipe.updateHop'; readonly payload: CommandPayloadMap['recipe.updateHop'] }
  | {
      readonly type: 'recipe.updateYeast';
      readonly payload: CommandPayloadMap['recipe.updateYeast'];
    }
  | {
      readonly type: 'recipe.updateMisc';
      readonly payload: CommandPayloadMap['recipe.updateMisc'];
    };

/** `kind:normalised name` → the entries spelled that way. */
type NameIndex = ReadonlyMap<string, readonly CustomIngredientDoc[]>;

function indexBy(
  entries: readonly CustomIngredientDoc[],
  spell: (entry: CustomIngredientDoc) => string,
): NameIndex {
  const index = new Map<string, CustomIngredientDoc[]>();
  for (const entry of entries) {
    const key = `${entry.kind}:${normalizeIngredientName(spell(entry))}`;
    index.set(key, [...(index.get(key) ?? []), entry]);
  }
  return index;
}

/**
 * Whether a lot's own maker column contradicts the entry it matched by name.
 *
 * `null` on the lot is not a contradiction: an unstated maker is the whole
 * reason this pass has work to do. Anything else must fold to the same string
 * as the entry's maker under the same normalisation names are compared with,
 * so "best malz" and "Best Malz" are one maker and "Best Malz" and "Weyermann"
 * are two. An entry whose kind carries no maker (a hop, a misc) reads as `''`
 * here, which agrees only with a lot that also names none.
 */
function makerAgrees(lotMaker: string | null, entry: CustomIngredientDoc): boolean {
  if (lotMaker === null) return true;
  return (
    normalizeIngredientName(lotMaker) ===
    normalizeIngredientName(customIngredientMaker(entry) ?? '')
  );
}

/** The one entry under `key`, or `null` when there are none or several (D-IL5). */
function unique(index: NameIndex, kind: InventoryKind, name: string): CustomIngredientDoc | null {
  const found = index.get(`${kind}:${normalizeIngredientName(name)}`);
  return found !== undefined && found.length === 1 ? (found[0] ?? null) : null;
}

/**
 * The commands that link everything already in the workspace that
 * unambiguously belongs to one of the brewer's ingredients (D1).
 *
 * ## Each side in its own convention
 *
 * A lot carries the bare `name` the library owns, with the maker in its own
 * column; a recipe row carries `customIngredientDisplayName(entry)` —
 * `"Weyermann Pilsner"` — because that is what the snapshot builder folded
 * onto it at add time (`bridge/snapshot.ts`), the maker having no column of
 * its own on a recipe item. So a lot is compared against the entry's bare
 * `name`, and a row against the entry's *display* name — never the other way,
 * and never one rule doing duty for both. Collapsing this to a single
 * comparison is the change that would look like a simplification and would
 * instead silently stop linking one side: a lot compared by display name
 * would miss every maker-bearing malt ("Weyermann Pilsner" the lot never
 * says), and a row compared by bare name would miss every one of those too
 * ("Pilsner" the row never says). That is the whole reason this feature
 * exists, so it is asserted here rather than assumed.
 *
 * ## Only when it cannot be anyone else (D-IL5)
 *
 * A name resolves only when exactly one entry of the matching kind is spelled
 * that way, kind included so a hop and a fermentable sharing a word never
 * cross. Two candidates, or none, leaves the item untouched — this pass never
 * guesses, and it never invents an ingredient to attach to.
 *
 * ## A maker the brewer typed is evidence, not decoration
 *
 * A lot is matched on its bare name, which is the only spelling it has; but a
 * lot also carries a maker in its own column, and the library entry it matched
 * carries one too. When those two disagree the name match is not a match at
 * all — it is a collision. A sack of Best Malz Pilsner and a library entry for
 * Weyermann Pilsner are the same *word* and two different malts, and linking
 * them would do more than attach an id: the step carries the entry's name and
 * maker (see below), so the reducer would write "Weyermann" over the "Best
 * Malz" the brewer typed, silently, at app load, on a background pass nobody
 * asked for. That is the one place this feature could destroy something the
 * brewer entered by hand, so it does not happen: a disagreeing maker skips the
 * lot and leaves it exactly as it was, to be linked by hand if the brewer
 * decides the two really are one ingredient.
 *
 * A lot with no maker of its own still links, and is the common case — the
 * pass exists for the sack typed as plain "Pilsner" before the library had an
 * entry for it. Absence contradicts nothing. And a hop or misc entry, whose
 * kind has no maker in the library at all, agrees only with a lot that also
 * names none, because writing `null` over a maker is the same erasure as
 * writing the wrong one.
 *
 * ## What a step carries (D-IL9)
 *
 * A lot's link carries the entry's name and maker alongside the id, all
 * three together. The reducer only fills name and maker from an ingredient on
 * `inventory.add`, never on `inventory.update` — an update changes exactly the
 * keys its `changes` object names — and `priorValues` builds the undo from
 * those same keys. So a link step that named only `ingredientId` would undo
 * back to the old link while leaving the new name and maker in place: a
 * cosmetic-looking omission that breaks undo silently. A row's link carries
 * the id alone, because a row keeps its own name and has no maker column to
 * disturb.
 *
 * ## Idempotent, ordered, pure
 *
 * A lot or row whose `ingredientId` is already set is skipped, so running the
 * pass again — on the same device, or an app load, or a second device racing
 * the first — proposes nothing: the plan converges to empty once the log has
 * caught up. Lots are walked in id order, then recipes in id order with rows
 * in list order, so two devices with the same log emit byte-identical
 * commands rather than merely equivalent ones. No clock is read, no id is
 * minted, no state is touched — the caller owns turning a step into a
 * timestamped, actor-stamped command and dispatching it.
 */
export function planIngredientLinks(state: WorkspaceState): readonly IngredientLinkStep[] {
  const entries = Object.values(state.customIngredients);
  const byBareName = indexBy(entries, (entry) => entry.name);
  const byDisplayName = indexBy(entries, customIngredientDisplayName);
  const steps: IngredientLinkStep[] = [];

  const lots = Object.values(state.inventory).sort((a, b) => a.id.localeCompare(b.id));
  for (const lot of lots) {
    if (lot.catalogRef !== null || lot.ingredientId !== null) continue;
    const entry = unique(byBareName, lot.kind, lot.name);
    if (entry === null) continue;
    if (!makerAgrees(lot.maker, entry)) continue;
    steps.push({
      type: 'inventory.update',
      payload: {
        itemId: lot.id,
        changes: { ingredientId: entry.id, name: entry.name, maker: customIngredientMaker(entry) },
      },
    });
  }

  const recipes = Object.values(state.recipes).sort((a, b) => a.id.localeCompare(b.id));
  for (const recipe of recipes) {
    const link = (
      kind: InventoryKind,
      item: {
        readonly itemId: string;
        readonly name: string;
        readonly catalogRef: unknown;
        readonly ingredientId: string | null;
      },
    ): string | null => {
      if (item.catalogRef !== null || item.ingredientId !== null) return null;
      return unique(byDisplayName, kind, item.name)?.id ?? null;
    };
    for (const item of recipe.fermentables) {
      const ingredientId = link('fermentable', item);
      if (ingredientId !== null) {
        steps.push({
          type: 'recipe.updateFermentable',
          payload: { recipeId: recipe.id, itemId: item.itemId, changes: { ingredientId } },
        });
      }
    }
    for (const item of recipe.hops) {
      const ingredientId = link('hop', item);
      if (ingredientId !== null) {
        steps.push({
          type: 'recipe.updateHop',
          payload: { recipeId: recipe.id, itemId: item.itemId, changes: { ingredientId } },
        });
      }
    }
    for (const item of recipe.yeasts) {
      const ingredientId = link('yeast', item);
      if (ingredientId !== null) {
        steps.push({
          type: 'recipe.updateYeast',
          payload: { recipeId: recipe.id, itemId: item.itemId, changes: { ingredientId } },
        });
      }
    }
    for (const item of recipe.miscs) {
      const ingredientId = link('misc', item);
      if (ingredientId !== null) {
        steps.push({
          type: 'recipe.updateMisc',
          payload: { recipeId: recipe.id, itemId: item.itemId, changes: { ingredientId } },
        });
      }
    }
  }

  return steps;
}
